<script setup lang="ts">
import type { Option } from './autocompleteTypes'
import { PhSpinner } from '@phosphor-icons/vue'
import { defineHubSearch } from '@piveau/sdk-vue'
import { useDebounce } from '@vueuse/core'
import { computed, ref, toRef } from 'vue'
import z from 'zod'
import AutocompleteMenu from './AutocompleteMenu.vue'

interface Props {
  by?: 'resource' | 'vocabulary' | ''
  baseUrl?: string
  vocabulary?: string
  resource?: string
  label?: string
  placeholder?: string
  errorMessage?: string
  name?: string
  setupFn?: (data: any) => Option
  disabled?: boolean
  isAutocompleteDisabled?: boolean
  postProcessFn?: (options: Option[]) => Option[]
  sort?: string
  authToken?: string
  searchIcon?: boolean
  limit?: number
  fixedCatalogFilter?: string
  enableFreeTextOption?: boolean
  freeTextOptionLabel?: (query: string) => string
}

const props = withDefaults(defineProps<Props>(), {
  by: '',
  baseUrl: 'https://twin.bydata.de/api/hub/search',
  label: '',
  placeholder: 'Type to search...',
  errorMessage: '',
  name: '',
  disabled: false,
  isAutocompleteDisabled: false,
  sort: 'title.de+asc,name.de+asc,pref_label.de+asc,pref_label.en+asc,alt_label.de+asc,alt_label.en+asc',
  authToken: '',
  searchIcon: false,
  limit: 24,
  fixedCatalogFilter: '',
  enableFreeTextOption: false,
  freeTextOptionLabel: (q: string) => `Search for "${q}"`,
})

interface FreetextItem {
  type: 'freetext'
  query: string
}

const emit = defineEmits<{
  'item-click': [value: Option | FreetextItem]
}>()

const query = defineModel<string>()

const isVocabularyMode = computed(() => !!props.vocabulary)
const resolvedBy = computed(() => props.by || isVocabularyMode.value ? 'vocabulary' : 'resource')
const resolvedIndex = computed(() => {
  if (props.resource === 'dataset')
    return 'dataset'
  if (isVocabularyMode.value)
    return 'vocabulary'
  return resolvedBy.value
})
const resolvedIndexDetails = computed(() => {
  if (props.resource === 'dataset')
    return 'datasets'
  if (isVocabularyMode.value)
    return 'vocabularies'
  return resolvedBy.value === 'resource' ? `resources/${props.resource}` : 'vocabularies'
})

const isAutocompleteEnabled = computed(() => Boolean(!props.isAutocompleteDisabled) && !!(props.resource || props.vocabulary))

const { useSearch } = defineHubSearch({
  baseUrl: props.baseUrl,
  index: resolvedIndex.value,
  indexDetails: resolvedIndexDetails.value,
  schema: z.any(),
}, (data: any) => {
  const setupData = props.setupFn?.(data)
  const id = String(setupData?.id || data?.resource || '')
  const name = String(setupData?.name || data?.pref_label?.de || data?.pref_label?.en || '')
  const returnData: Option = {
    ...setupData,
    id,
    name,
  }
  return returnData
})

const debouncedQuery = useDebounce(query, 300)

const vocabularyRef = computed(() => props.vocabulary)

const headers = computed(() => {
  return {
    ...props.authToken && { Authorization: `Bearer ${props.authToken}` },
  }
})

const facetsComputed = computed(() => {
  if (props.fixedCatalogFilter) {
    return { open_data_presence: [`${props.fixedCatalogFilter}.bydata`] }
  }
  return {}
})

const { getSearchResultsEnhanced, isFetching: _isSearchLoading } = useSearch({
  queryOptions: {
    enabled: isAutocompleteEnabled,
  },
  queryParams: {
    q: debouncedQuery,
    limit: toRef(props.limit),
    vocabulary: computed(() => isVocabularyMode.value ? vocabularyRef.value : ''),
    sort: toRef(props.sort),
    facets: facetsComputed,
  },
  additionalParams: computed(() => ({
    resource: resolvedBy.value === 'resource' ? props.resource : '',
  })),
  headers,
})

const searchResults = computed<Option[]>(() => {
  let results = getSearchResultsEnhanced.value || []
  if (props.postProcessFn) {
    results = props.postProcessFn(results)
  }
  return results
})

const isSearchLoading = useDebounce(_isSearchLoading, 350)

function handleItemClick(item: Option | FreetextItem) {
  emit('item-click', item)
}
</script>

<template>
  <AutocompleteMenu
    :options="searchResults"
    :label="label"
    :error-message="errorMessage"
    :placeholder="placeholder"
    :name="name"
    :loading="isSearchLoading"
    :search-icon="props.searchIcon"
    :enable-free-text-option="props.enableFreeTextOption"
    :free-text-option-label="props.freeTextOptionLabel"
    @item-click="handleItemClick"
    v-model="query"
  >
    <template #label>
      <div class="autocomplete-vocab-menu__label">
        {{ label }}
        <PhSpinner v-if="isSearchLoading" class="animate-spin" />
      </div>
    </template>
    <template v-if="$slots.option" #option="{ option, active }">
      <slot name="option" :option="option" :active="active" />
    </template>
  </AutocompleteMenu>
</template>

<style scoped>
.autocomplete-vocab-menu__label {
  display: flex;
  align-items: center;
  gap: .5rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
